"use strict";
/*************************************************************************
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it. If you have received this file from a source other than Adobe,
 * then your use, modification, or distribution of it requires the prior
 * written permission of Adobe.
 **************************************************************************/
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * APIs that simplify code to make authenticated network requests for resources, execute GraphQL
 * queries, etc. and optimize them through efficient batching when appropriate.
 *
 * ***Import:***
 *
 * ```typescript
 * import {batch, fetch, query} from '@adobe/exc-app/network';
 * ```
 *
 * ***Default export:***
 *
 * [NetworkApi](../interfaces/network.networkapi.md)
 *
 * ***Usage:***
 *
 * ```typescript
 * import {batch, fetch, query} from '@adobe/exc-app/network';
 *
 * // Performs a window.fetch call with Authorization and x-api-key headers set
 * const fetchResponse = await fetch('https://localhost', {auth: 'Header', method: 'GET'});
 *
 * // Executes a query for resources to the ExC GraphQL service
 * const queryResponse = await query({
 *   data: {
 *     query: `
 *       query userBehanceQuery($userId: String!, $apiKey: String!) {
 *         userBehance(userId: $userId, apiKey: $apiKey) {
 *           images
 *         }
 *       }`,
 *     variables: {
 *       apiKey: 'test-app',
 *       userId: '123@AdobeID'
 *     }
 *   },
 *   operationName: 'BehanceAvatar'
 * });
 *
 * // Calls made that get batched into a single network call
 * const [batchResponse1, batchResponse2] = await Promise.all([
 *   batch('https://localhost/api1', {auth: 'Header', priority: 'P1'}),
 *   batch('https://example.com/api2', {auth: 'Header', priority: 'P1'})
 * ]);
 * ```
 * @packageDocumentation
 * @module network
 */
const Global_1 = require("./src/Global");
/**
 * ENUM which module developers can use for giving the preferred region.
 */
/**
 * @ignore
 */
var ServiceDCRegions;
(function (ServiceDCRegions) {
    ServiceDCRegions["azureEastUS"] = "va7";
    ServiceDCRegions["azureWestUS"] = "va7";
    ServiceDCRegions["azureCentralUS"] = "va7";
    ServiceDCRegions["azureWestEurope"] = "nld2";
    ServiceDCRegions["azureCentralEurope"] = "nld2";
    ServiceDCRegions["azureEastEurope"] = "nld2";
    ServiceDCRegions["azureEastAustralia"] = "aus5";
    ServiceDCRegions["azureWestAustralia"] = "aus5";
    ServiceDCRegions["azureCentralAustralia"] = "aus5";
    ServiceDCRegions["azureSouthEastAsia"] = "aus5";
    ServiceDCRegions["azureEastChina"] = "aus5";
    ServiceDCRegions["azureCentralIndia"] = "aus5";
    ServiceDCRegions["azureEastJapan"] = "aus5";
})(ServiceDCRegions = exports.ServiceDCRegions || (exports.ServiceDCRegions = {}));
/**
 * Provides an interface for fetching resources powered by the global 'fetch' API.
 *
 * ***Example:***
 *
 * ```typescript
 * // performs a window.fetch call
 * let response = await fetch('https://example.com/api/ping');
 *
 * // performs a window.fetch call with Authorization and x-api-key headers set
 * response = await fetch('https://localhost', {auth: 'Header', method: 'GET'});
 *
 * // performs a window.fetch call with user_token and client_id query parameters added to the URL
 * const request = new Request('https://localhost', {
 *   body: JSON.stringify({k: 'v'}),
 *   headers: new Headers(),
 *   method: 'POST'
 * });
 * response = await fetch(request, {auth: 'Header'});
 * ```
 * @param input The resource that you wish to fetch. It can either be the URL of the resource you
 * want to fetch or a Request object.
 * @param init An object containing any custom settings that you want to apply to the request.
 * @returns The promise for the response to the fetch operation.
 */
function fetch(input, init) {
    return Global_1.getImpl('network').fetch(input, init);
}
exports.fetch = fetch;
/**
 * Provides an interface for querying resources via GraphqQL.
 * In order to consume query, please make sure the respective query resolver is
 * available in the GraphQL Service.
 * ***Example:***
 *
 * ```typescript
 * const BEHANCE_QUERY = `
 *   query userBehanceQuery($userId: String!, $apiKey: String!) {
 *     userBehance(userId: $userId, apiKey: $apiKey) {
 *       images
 *     }
 *   }`;
 *
 * // queries the respective resource via GraphQL and returns HTTP Response {ok: true, status: 200, ...}
 * query({data: {query: BEHANCE_QUERY, variables: {
 *   userId: '123@AdobeID',
 *   apiKey: 'test-app',
 * }}, operationName: 'BehanceAvatar'});
 *
 * // queries the respective resource via GraphQL and returns HTTP Response {ok: true, status: 200, ...}
 * query({data: {query: `
 *   query userBehanceQuery {
 *     userBehance(userId: "123@AdobeID", apiKey: "test-app") {
 *       images
 *     }
 *   }`
 * }});
 * ```
 * @param input Query request containing desired GQL Query.
 * @returns The promise for the response to the query operation.
 */
function query(input) {
    return Global_1.getImpl('network').query(input);
}
exports.query = query;
/**
 * Fetches a resource by batching it with other calls to batch made with the same `priority` at
 * around the same time. For example, below two API calls would get combined into a single network
 * call.
 *
 * This is achieved by converting multiple calls to the `batch` API into a GraphQL query that the
 * ExC GraphQL service has a built-in resolver for.
 *
 * ***Example:***
 *
 * ```typescript
 * // Batching two resource fetches into a single network call
 * const [batchResponse1, batchResponse2] = await Promise.all([
 *   batch('https://localhost/api1', {auth: 'Header', priority: 'P1'}),
 *   batch('https://example.com/api2', {auth: 'Header', priority: 'P1'})
 * ]);
 *
 * const request = new Request('https://localhost', {
 *   body: JSON.stringify({k: 'v'}),
 *   headers: new Headers(),
 *   method: 'POST'
 * });
 *
 * // batches HTTP resources with P2(default) priority debouncing level and without Authentication token, API Key
 * // and returns HTTP Response {ok: true, status: 200, ...}.
 * batch(request);
 *
 * // batches HTTP resources with P3(lowest) priority debouncing level and Authentication token, API Key in Query params
 * // and returns HTTP Response {ok: true, status: 200, ...}.
 * batch(request, {auth: 'Header', priority: 'P3'});
 * ```
 * @param input The resource that you wish to fetch. It can either be the URL of the resource you want to fetch or a Request object.
 * @param init An object containing any custom settings that you want to apply to the request.
 * @returns The promise for the response to the batch operation.
 */
function batch(input, init) {
    return Global_1.getImpl('network').batch(input, init);
}
exports.batch = batch;
/**
 * @ignore
 */
class Internal {
    static configure(config) {
        return Global_1.getImpl('network').configure(config);
    }
}
exports.Internal = Internal;
//# sourceMappingURL=network.js.map